import React, { useEffect, forwardRef, useImperativeHandle, useLayoutEffect, } from 'react';
import { createPortal } from 'react-dom';
import { useFloating, offset, flip, autoUpdate } from '@floating-ui/react-dom';
import { useOutsideClick } from './useOutsideClick';
import { useDidMount } from './useDidMount';
export const Portal = forwardRef((props, ref) => {
    const didMount = useDidMount();
    const { x, y, update, strategy, placement, refs } = useFloating({
        placement: props.placement ?? 'bottom-start',
        middleware: [
            offset({
                mainAxis: props.offsetMain ?? 0,
                crossAxis: props.offsetCross ?? 0,
            }),
            flip({
                mainAxis: !props.restrictFlipMain,
                crossAxis: !props.restrictFlipCross,
            }),
        ],
    });
    const { setFloating, setReference } = refs;
    useLayoutEffect(() => {
        if (props.reference?.current) {
            setReference(props.reference.current);
        }
    }, [props.reference]);
    useImperativeHandle(ref, () => ({
        update,
    }), []);
    useEffect(() => {
        update();
        if (props.onPlacementChange) {
            props.onPlacementChange(placement);
        }
    }, [placement]);
    useEffect(() => {
        if (!refs.reference.current || !refs.floating.current || !didMount) {
            return;
        }
        return autoUpdate(refs.reference.current, refs.floating.current, update);
    }, [didMount]);
    useOutsideClick(refs.floating, (event) => {
        if (props.onOutClick) {
            props.onOutClick(event);
        }
    });
    const styles = {
        top: '0',
        left: '0',
        zIndex: props.zIndex ?? 5,
        transform: `translate3d(${Math.round(x ?? 0)}px,${Math.round(y ?? 0)}px, 0)`,
        minWidth: props.fullWidth ? refs.reference?.current?.offsetWidth : 0,
        pointerEvents: props.pointerEventsNone ? 'none' : 'auto',
    };
    if (props.renderInside) {
        return (React.createElement("div", { ref: setFloating, style: {
                position: 'absolute',
                ...styles,
            } }, props.children));
    }
    return createPortal(React.createElement("div", { ref: setFloating, style: {
            position: strategy,
            ...styles,
        } }, props.children), document.body);
});
//# sourceMappingURL=Portal.js.map